import React, { createContext, useContext, useReducer, useRef, useCallback, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ServiceStep, ImageUpload } from "../../../../../utilities/types/unified-services";
import { useUnifiedService } from "../../../../../utilities/contexts/UnifiedServiceContext";
import { useDeviceOrientation } from "../hooks/useDeviceOrientation";
import { useSoundPlayer } from "../hooks/useSoundPlayer";
import { useCameraCapture } from "../hooks/useCameraCapture";
import { cameraReducer, initialState } from "../reducer";
import { CAMERA_CONFIG, STEP_CONFIG } from "../constants";
import type { CameraState, CameraAction } from "../types";

interface CameraContextType {
  state: CameraState;

  isSupported: boolean;
  hasPermission: boolean | null;
  orientation: {
    alpha: number | null;
    beta: number | null;
    gamma: number | null;
  };

  dispatch: React.Dispatch<CameraAction>;
  handleCapture: (webcamRef: React.RefObject<any>) => Promise<void>;
  handleRequestPermission: () => Promise<void>;
  handleContinueWithoutPermission: () => void;
  handleRetry: () => void;
  handleGoBack: () => void;
  handleUseUpload: () => void;
  handleCameraReady: () => void;
  handleCameraError: (error: any) => void;
  handleToggleCamera: () => void;

  handleOrientationCorrect: () => void;
  handleOrientationIncorrect: () => void;

  isOrientationCorrect: (beta: number | null) => boolean;
  getCurrentConfig: () => typeof STEP_CONFIG.front | typeof STEP_CONFIG.side;
}

const CameraContext = createContext<CameraContextType | null>(null);

interface CameraProviderProps {
  children: ReactNode;
}

export const CameraProvider: React.FC<CameraProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cameraReducer, initialState);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const captureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastOrientationRef = useRef<boolean>(false);
  const captureInProgressRef = useRef<boolean>(false);
  const isPlayingDegreeSound = useRef<boolean>(false);
  const lastStepSoundPlayed = useRef<string | null>(null);

  const navigate = useNavigate();
  const { serviceInterface, bodyMeasurement, resetAll } = useUnifiedService();
  const { setCurrentStep } = serviceInterface;
  const { orientation, hasPermission, requestPermission, isSupported } = useDeviceOrientation();
  const { playSound, stopAllSounds, stopDegreeSound, cleanup: cleanupSounds } = useSoundPlayer();
  const { captureImage, dataUrlToFile } = useCameraCapture();

  const isOrientationCorrect = useCallback((beta: number | null): boolean => {
    if (beta === null) return false;
    return beta >= CAMERA_CONFIG.ORIENTATION_THRESHOLD.min && beta <= CAMERA_CONFIG.ORIENTATION_THRESHOLD.max;
  }, []);

  const getCurrentConfig = useCallback(() => {
    if (state.currentStep === "front") return STEP_CONFIG.front;
    if (state.currentStep === "side") return STEP_CONFIG.side;

    return STEP_CONFIG.front;
  }, [state.currentStep]);

  useEffect(() => {
    if (state.captureMode === "manual" && (state.currentStep === "front" || state.currentStep === "side") && !state.isLoading && !state.cameraError) {
      const soundKey = state.currentStep === "front" ? "frontView" : "sideView";
      const stepSoundKey = `manual-${state.currentStep}-${soundKey}`;

      if (lastStepSoundPlayed.current !== stepSoundKey) {
        setTimeout(() => {
          playSound(soundKey);
          lastStepSoundPlayed.current = stepSoundKey;
        }, 500);
      }
    }
  }, [state.currentStep, state.captureMode, state.isLoading, state.cameraError]);

  useEffect(() => {
    isPlayingDegreeSound.current = false;
    lastStepSoundPlayed.current = null;
    captureInProgressRef.current = false;

    stopAllSounds();
    stopDegreeSound();

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      dispatch({ type: "STOP_TIMER" });
    }

    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
      captureTimeoutRef.current = null;
    }
  }, [state.currentStep]);

  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    let currentCount = CAMERA_CONFIG.TIMER_DURATION;

    timerRef.current = setInterval(() => {
      currentCount -= 1;
      dispatch({ type: "UPDATE_TIMER", payload: currentCount });
    }, 1000);
  }, [state.currentStep, state.captureMode]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch({ type: "STOP_TIMER" });
  }, [state.currentStep, state.showTimer]);

  const handleOrientationCorrect = useCallback(() => {
    if (
      state.captureMode === "auto" &&
      !captureInProgressRef.current &&
      !state.showTimer &&
      !state.isLoading &&
      !state.cameraError &&
      (state.currentStep === "front" || state.currentStep === "side")
    ) {
      stopAllSounds();
      stopDegreeSound();
      isPlayingDegreeSound.current = false;

      captureInProgressRef.current = true;
      const soundKey = state.currentStep === "front" ? "frontView" : "sideView";
      const stepSoundKey = `auto-${state.currentStep}-${soundKey}`;

      lastStepSoundPlayed.current = null;

      setTimeout(() => {
        if (captureInProgressRef.current) {
          playSound(soundKey);
          lastStepSoundPlayed.current = stepSoundKey;
        }
      }, 100);

      captureTimeoutRef.current = setTimeout(() => {
        if (captureInProgressRef.current) {
          playSound("timer");
          dispatch({ type: "START_TIMER", payload: CAMERA_CONFIG.TIMER_DURATION });
          startTimer();
        }
      }, CAMERA_CONFIG.CAPTURE_START_DELAY);
    }
  }, [state.captureMode, state.currentStep, state.showTimer, state.isLoading, state.cameraError, playSound, startTimer, stopAllSounds]);

  const handleOrientationIncorrect = useCallback(() => {
    if (state.captureMode === "auto" && (state.currentStep === "front" || state.currentStep === "side")) {
      if (captureInProgressRef.current) {
        if (captureTimeoutRef.current) {
          clearTimeout(captureTimeoutRef.current);
          captureTimeoutRef.current = null;
        }

        if (state.showTimer) {
          stopTimer();
        }

        captureInProgressRef.current = false;
        lastStepSoundPlayed.current = null;
        stopAllSounds(true);
      }

      if (!isPlayingDegreeSound.current) {
        isPlayingDegreeSound.current = true;
        playSound("degree");
      }
    }
  }, [state.captureMode, state.currentStep, state.showTimer, playSound, stopTimer, stopAllSounds]);

  const handleCapture = useCallback(
    async (webcamRef: React.RefObject<any>) => {
      if (state.captureMode === "manual") {
        stopAllSounds();
      }

      const image = await captureImage(webcamRef);
      if (!image) {
        dispatch({ type: "SET_ERROR", payload: "Failed to capture image. Please try again." });
        return;
      }

      if (state.currentStep === "front") {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        stopAllSounds();
        stopDegreeSound();

        dispatch({ type: "COMPLETE_FRONT_CAPTURE", payload: image });

        lastOrientationRef.current = false;
        captureInProgressRef.current = false;
        isPlayingDegreeSound.current = false;
        lastStepSoundPlayed.current = null;
      } else if (state.currentStep === "side") {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        stopAllSounds();
        stopDegreeSound();

        dispatch({ type: "COMPLETE_SIDE_CAPTURE", payload: image });

        captureInProgressRef.current = false;
        isPlayingDegreeSound.current = false;
        lastStepSoundPlayed.current = null;
      }
    },
    [captureImage, state.currentStep, state.captureMode, stopAllSounds]
  );

  const processImages = useCallback(async () => {
    try {
      if (!state.frontImage || !state.sideImage) return;

      const frontFile = dataUrlToFile(state.frontImage.dataUrl, "front-image.jpg");
      const sideFile = dataUrlToFile(state.sideImage.dataUrl, "side-image.jpg");

      const createImageUpload = (file: File, base64: string): ImageUpload => ({
        file,
        base64,
        preview: URL.createObjectURL(file),
        isValid: true,
        error: undefined,
        blurredBase64: null,
      });

      const frontImageUpload = createImageUpload(frontFile, state.frontImage.base64);
      const sideImageUpload = createImageUpload(sideFile, state.sideImage.base64);

      bodyMeasurement.setFrontImage(frontImageUpload);
      bodyMeasurement.setSideImage(sideImageUpload);

      setCurrentStep(ServiceStep.FACE_BLUR);
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to process images. Please try again." });
    }
  }, [state.frontImage, state.sideImage, dataUrlToFile, bodyMeasurement, setCurrentStep]);

  const handleRequestPermission = useCallback(async () => {
    dispatch({ type: "SET_PERMISSION_LOADING", payload: true });

    try {
      const granted = await requestPermission();
      dispatch({ type: "SET_PERMISSION", payload: granted });
    } catch (error) {
      dispatch({ type: "SET_PERMISSION", payload: false });
    }
  }, [requestPermission, isSupported]);

  const handleContinueWithoutPermission = useCallback(() => {
    dispatch({ type: "SET_PERMISSION", payload: false });
  }, []);

  const handleCameraReady = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: false });
    dispatch({ type: "SET_CAMERA_ERROR", payload: null });

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === "videoinput");
      const hasBackCamera =
        videoDevices.length > 1 ||
        videoDevices.some((device) => device.label.toLowerCase().includes("back") || device.label.toLowerCase().includes("rear") || device.label.toLowerCase().includes("environment"));

      dispatch({ type: "SET_HAS_BACK_CAMERA", payload: hasBackCamera });
    } catch (error) {
      dispatch({ type: "SET_HAS_BACK_CAMERA", payload: false });
    }
  }, []);

  const handleCameraError = useCallback((error: any) => {
    let errorMessage = "Camera not available. Please check your camera connection.";

    if (error?.toString().includes("denied")) {
      errorMessage = "Camera access denied. Please allow camera access in your browser settings.";
    } else if (error?.name === "NotReadableError") {
      errorMessage = "Camera is being used by another application. Please close other apps and try again.";
    } else if (error?.name === "OverconstrainedError") {
      errorMessage = "Selected camera is not available. Switching to default camera.";

      dispatch({ type: "SET_LOADING", payload: true });
      setTimeout(() => {
        dispatch({ type: "TOGGLE_CAMERA" });
      }, 100);
      return;
    }

    dispatch({ type: "SET_CAMERA_ERROR", payload: errorMessage });
  }, []);

  const handleRetry = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const handleGoBack = useCallback(() => {
    resetAll();
  }, [resetAll]);

  const handleUseUpload = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });

    bodyMeasurement.setCameraOptedOut(true);
  }, [bodyMeasurement]);

  const handleToggleCamera = useCallback(() => {
    if (state.hasBackCamera && !state.isLoading) {
      dispatch({ type: "SET_LOADING", payload: true });

      dispatch({ type: "TOGGLE_CAMERA" });
    }
  }, [state.hasBackCamera, state.isLoading]);

  useEffect(() => {
    dispatch({ type: "SET_ORIENTATION_SUPPORTED", payload: isSupported });

    stopAllSounds();
    stopDegreeSound();
    isPlayingDegreeSound.current = false;
    lastStepSoundPlayed.current = null;
    captureInProgressRef.current = false;

    if (isSupported && hasPermission === true) {
      dispatch({ type: "SET_CAPTURE_MODE", payload: "auto" });
    } else {
      dispatch({ type: "SET_CAPTURE_MODE", payload: "manual" });

      if (!isSupported && state.currentStep === "permission") {
        dispatch({ type: "SET_PERMISSION", payload: false });
      }
    }

    const checkCameraAvailability = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === "videoinput");
        const hasBackCamera = videoDevices.length > 1;
        dispatch({ type: "SET_HAS_BACK_CAMERA", payload: hasBackCamera });
      } catch (error) {
        dispatch({ type: "SET_HAS_BACK_CAMERA", payload: false });
      }
    };

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      checkCameraAvailability();
    }
  }, [isSupported, hasPermission]);

  useEffect(() => {
    if (isSupported && hasPermission === null && typeof (DeviceOrientationEvent as any).requestPermission === "function") {
      if (state.currentStep !== "permission") {
        dispatch({ type: "FORCE_PERMISSION_STEP" });
      }
    }
  }, [isSupported, hasPermission, state.currentStep]);

  useEffect(() => {
    if (state.captureMode !== "auto" || hasPermission !== true) return;

    dispatch({
      type: "SET_ORIENTATION",
      payload: {
        beta: orientation.beta,
        alpha: orientation.alpha,
        gamma: orientation.gamma,
      },
    });

    const isCorrect = isOrientationCorrect(orientation.beta);

    if (lastOrientationRef.current !== isCorrect) {
      lastOrientationRef.current = isCorrect;

      dispatch({ type: "SET_ORIENTATION_CORRECT", payload: isCorrect });

      if (state.captureMode === "auto" && state.currentStep !== "permission" && state.currentStep !== "processing") {
        if (isCorrect) {
          handleOrientationCorrect();
        } else {
          handleOrientationIncorrect();
        }
      }
    }
  }, [orientation.beta, orientation.alpha, orientation.gamma, hasPermission, isOrientationCorrect, state.currentStep, state.captureMode, state.showTimer, state.isCapturing]);

  useEffect(() => {
    if (state.frontImage && state.sideImage && state.currentStep === "processing") {
      processImages();
    }
  }, [state.frontImage, state.sideImage, state.currentStep]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }

      captureInProgressRef.current = false;
      isPlayingDegreeSound.current = false;
      lastOrientationRef.current = false;
      lastStepSoundPlayed.current = null;

      cleanupSounds();
    };
  }, []);

  const contextValue: CameraContextType = {
    state,

    isSupported,
    hasPermission,
    orientation,

    dispatch,
    handleCapture,
    handleRequestPermission,
    handleContinueWithoutPermission,
    handleRetry,
    handleGoBack,
    handleUseUpload,
    handleCameraReady,
    handleCameraError,
    handleToggleCamera,

    handleOrientationCorrect,
    handleOrientationIncorrect,

    isOrientationCorrect,
    getCurrentConfig,
  };

  return <CameraContext.Provider value={contextValue}>{children}</CameraContext.Provider>;
};

export const useCameraContext = (): CameraContextType => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error("useCameraContext must be used within a CameraProvider");
  }
  return context;
};
